import "./MapViewerElement.less";
import * as olProj from 'ol/proj';
import Feature from 'ol/Feature';
import Icon from 'ol/style/Icon';
import Map from 'ol/Map';
import Point from 'ol/geom/Point';
import Polyline from 'ol/format/Polyline.js';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import TileLayer from 'ol/layer/Tile';
import Vector from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import { Attribution, Zoom } from 'ol/control';
import { Geometry, LineString } from 'ol/geom';
import { HistoryStore } from 'Feature/History';
import { LocationStore, VehicleLocation } from "./LocationStore";
import { MapOptionsElement } from './MapOptionsElement';
import { OptionsStore } from './OptionsStore';
import { VehicleListElement } from './VehicleListElement';

interface TrackedVehicle {
    vectorLayer: VectorLayer<VectorSource<Point>>;
}

class MapViewerElement extends HTMLElement {
    private static _marker = `<svg width="700" height="46" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 185.2 12.2"><g fill="$color$" stoke="#000"><circle cx="92.6" cy="6.1" r="2.1" stroke-linecap="round" stroke-width=".3"/><text xml:space="preserve" x="112.3" y="9.4" stroke-width=".1" font-family="Arial" font-size="11.9" style="line-height:1.25;-inkscape-font-specification:Arial" transform="scale(.8667 1.1538)"><tspan x="112.3" y="9.4" font-weight="400" style="-inkscape-font-specification:Arial">$text$</tspan></text></g></svg>`;
    static tag = "map-viewer";
    private formatIcon = (color: string, text: string, svg: string) => `data:image/svg+xml;utf8,${svg.replace("$text$", text).replace("$color$", color).replace(/\#/g, '%23')}`;
    private _vehicleList: VehicleListElement;
    private _vehicles: { [key: string]: TrackedVehicle } = {};
    private _map: Map;
    private _options: MapOptionsElement;
    private _routeLayers: VectorLayer<VectorSource<Geometry>>[] = [];

    connectedCallback() {
        this.renderMap();
        LocationStore.instance.addEventListener("source-updated", this.sourceUpdatedHandler);
        (async () => await this.connectedCallbackAsync())();

        this._vehicleList.addEventListener("vehicle-selected", this.vehicleSelectedHandler);

        OptionsStore.instance.addEventListener("toggle-routes", this.toggleRoutesHandler);
    }

    private toggleRoutesHandler = async (e: CustomEvent<boolean>) => { 
        if (e.detail)
            await this.showHistory();
        else
            this.removeHistory();
    }

    private vehicleSelectedHandler = (e: CustomEvent<string>) => {
        var location = LocationStore.instance.getLocation(e.detail);
        if (location) {
            this._map.getView().setCenter(olProj.fromLonLat([location.longitude, location.latitude]));
            this._map.getView().setZoom(12);
        }
    }

    private sourceUpdatedHandler = (event: CustomEvent<VehicleLocation>) => {
        const location = event.detail;
        const existing = this._vehicles[location.deviceId];
        if (existing)
            existing.vectorLayer.getSource().getFeatures()[0].getGeometry().setCoordinates(olProj.fromLonLat([location.longitude, location.latitude]));
        else
            this._vehicles[location.deviceId] = { vectorLayer: this.createLayer(location, MapViewerElement._marker) };
    }

    private updateText = (routeName: string, layer: VectorLayer<VectorSource<Point>>, text: string) => {
        layer.setStyle(new Style({
            image: new Icon({
                opacity: 1,
                src: this.formatIcon(LocationStore.routeToColor(routeName), text, MapViewerElement._marker),
                scale: 0.3
            })
        }));
    }

    private getText = (location: VehicleLocation) => {
        if (location) {
            const time = this.readableTimespan(new Date(location.created));
            return `${location.name} ${time}`;
        } else
            return location.name;
    }

    private textUpdateLoop = () => {
        for (var key in this._vehicles) {
            const location = LocationStore.instance.getLocation(key);

            if (location) {
                this.updateText(location.name, this._vehicles[key].vectorLayer, this.getText(location));
            }
        }
        setTimeout(this.textUpdateLoop, 1000 * 60);
    }

    private readableTimespan = (date: Date) => {
        let diffTime = Math.abs(new Date().valueOf() - date.valueOf());
        let days = diffTime / (24 * 60 * 60 * 1000);
        let hours = (days % 1) * 24;
        let minutes = (hours % 1) * 60;
        let secs = (minutes % 1) * 60;
        [days, hours, minutes, secs] = [Math.floor(days), Math.floor(hours), Math.floor(minutes), Math.floor(secs)]

        if (days > 1)
            return `(${days} dage)`;

        if (hours > 1)
            return `(${hours} timer)`;

        if (minutes > 1)
            return `(${minutes} min)`;

        return "";
    }

    private createLayer(location: VehicleLocation, svg?: string) {
        var style = new Style({
            image: new Icon({
                opacity: 1,
                src: this.formatIcon(LocationStore.routeToColor(location.name), this.getText(location), svg || MapViewerElement._marker),
                scale: 0.3
            })
        });


        var layer = new VectorLayer({
            source: new VectorSource({
                features: [
                    new Feature({
                        geometry: new Point(olProj.fromLonLat([location.longitude, location.latitude])),

                    })
                ]
            }),
            style: style
        });

        this._map.addLayer(layer);
        return layer;
    }

    protected async connectedCallbackAsync() {
        await LocationStore.instance.start();
        this.textUpdateLoop();
    }

    private renderMap = () => {
        var attribution = new Attribution({
            collapsible: false,
            collapsed: false
        });

        var zoomControl = new Zoom({
            zoomInTipLabel: 'Zoom ind',
            zoomOutTipLabel: 'Zoom ud',
            className: 'ol-zoom custom-zoom-control'
        });

        this._map = new Map({
            target: this,
            layers: [
                new TileLayer({
                    source: new XYZ({
                        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
                        attributions: '© <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
                    })
                })
            ],
            controls: [zoomControl, attribution],
            view: new View({
                center: olProj.fromLonLat([10.3162, 56.0268]),
                zoom: 8
            })
        });


        this.append(this._vehicleList = new VehicleListElement());
        this.append(this._options = new MapOptionsElement());
        

        if (OptionsStore.instance.showRoutes)
            this.showHistory();
    }

    private showHistory = async () => { 
        const history = await HistoryStore.instance.initial();

        const routes = Object.keys(history);

        for (const route of routes) {
            if (!history[route])
                continue;
            
            try { 
                await this.showRouteHistory(route, history[route]);
            }
            catch (e) {
                console.log(e);
            }   
        }

        LocationStore.instance.addEventListener("source-updated", this.updateHistoryForVehicle);
    }


    private updateHistoryForVehicle = (event: CustomEvent<VehicleLocation>) => {
        const geometry = this._geometries[event.detail.name];
        if (!geometry)
            return;

        geometry.appendCoordinate(olProj.fromLonLat([event.detail.longitude, event.detail.latitude]));
    }

    private showRouteHistory = async (route: string, polyline: string) => { 
        polyline = encodeURI(polyline);

        const baseUrl = (`https://router.project-osrm.org/route/v1/car/polyline(${polyline})`);
        const response = await fetch(baseUrl.toString());

        const routeData = await response.json();

        if (routeData.code !== "Ok")
            return;

        if (!routeData.routes[0])
            return;

        const geometry = new Polyline().readGeometry(routeData.routes[0].geometry, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
        }) as LineString;

        this._geometries[route] = geometry;
        
        const feature = new Feature({
            type: 'route',
            geometry: geometry
        });
    

        const source = new Vector();
        source.addFeature(feature);

        var layer = new VectorLayer({
            source,
            style: new Style({
                stroke: new Stroke({
                    color: LocationStore.routeToColor(route),
                    width: 2
                })
            })
        });

        this._map.addLayer(layer);
        this._routeLayers.push(layer);
    }

    private _geometries: { [key: string]: LineString } = {};

    private removeHistory = () => { 
        if (this._routeLayers) { 
            for (const layer of this._routeLayers) { 
                this._map.removeLayer(layer);
            }
        }

        this._routeLayers = [];
        this._geometries = {};

        LocationStore.instance.removeEventListener("source-updated", this.updateHistoryForVehicle);
    }


}

customElements.define(MapViewerElement.tag, MapViewerElement);